import { theaterOrganizationRouteNames } from '../../../../routing/routeNames/theaterOrganizerRouterNames'

const {
  theaterOrganizationEvents,
  theaterLiveEvents,
  theaterPastEvents,
  theaterOrganizationAddEvent,
  theaterOrganizationProfileInfo,
  theaterOrganizationFinanceInfo,
  theaterOrganizationSubscribers,
  theaterOrganizationGenres,
  theaterOrganizationTags,
  theaterOrganizationSubscriptionsPlan,
  theaterOrganizationSubscriptionsList,
  theaterOrganizationTicketType,
  theaterOrganizationMailingList,
  theaterOrganizationPromoCodes,
  theaterOrganizationPromoCodeInfo,
  theaterOrganizationMemberships,
  theaterOrganizationMembersList,
  theaterOrganizationReports,
} = theaterOrganizationRouteNames

/**
 * Sidebar keys for mapping routes to sidebar links
 * Some are string literals because for those links there isn't any route, they are just dropdowns for their subroutes
 *
 * They are in order as they are in Sidebar
 */
const sidebarKeys = {
  events: theaterOrganizationEvents,
  liveEvents: theaterLiveEvents,
  pastEvents: theaterPastEvents,
  organizationInfo: theaterOrganizationProfileInfo,
  financeInfo: theaterOrganizationFinanceInfo,
  subscribers: theaterOrganizationSubscribers,
  genres: theaterOrganizationGenres,
  tags: theaterOrganizationTags,
  subscriptionsPlan: theaterOrganizationSubscriptionsPlan,
  subscriptionsList: theaterOrganizationSubscriptionsList,
  ticketType: theaterOrganizationTicketType,
  mailingList: theaterOrganizationMailingList,
  promoCodes: theaterOrganizationPromoCodes,
  memberships: theaterOrganizationMemberships,
  membersList: theaterOrganizationMembersList,
  reports: theaterOrganizationReports,
}

/**
 * Sidebar links - objects for sidebar configuration
 *
 * Type:
 * {
 *    label: Name displayed in sidebar
 *    icon: Name of icon, representing the one inside assets/sidebar-icons
 *    key: Key for mapping the link to sidebar, take from sidebarKeys
 *    modulesRequired?: List of modules required for displaying this link
 *    allowedRoles?: List of roles for which this link will be displayed
 *    linkedRoutes: List of all routes for which this link will be highlighted as active and disabled for clicking (including its internal route)
 *    internalRoute: {
 *      name: Name of route this link will lead to, take from routeNames object
 *    }
 *    subroutes? List of route objects, only dropdowns have this property
 * }
 */
export const theaterOrganizationSidebarConfiguration = [
  {
    label: 'My events',
    key: sidebarKeys.events,
    icon: 'events',
    subroutes: [
      {
        label: 'Live events',
        key: sidebarKeys.liveEvents,
        linkedRoutes: [theaterLiveEvents],
        internalRoute: {
          name: theaterLiveEvents,
        },
      },
      {
        label: 'Past events',
        key: sidebarKeys.pastEvents,
        linkedRoutes: [theaterPastEvents],
        internalRoute: {
          name: theaterPastEvents,
        },
      },
    ],
    linkedRoutes: [
      theaterOrganizationEvents,
      theaterOrganizationAddEvent,
      theaterLiveEvents,
      theaterPastEvents,
    ],
    internalRoute: {
      name: theaterLiveEvents,
    },
  },
  {
    label: 'Organization info',
    key: sidebarKeys.organizationInfo,
    icon: 'organization-info',
    linkedRoutes: [theaterOrganizationProfileInfo],
    internalRoute: {
      name: theaterOrganizationProfileInfo,
    },
  },
  {
    label: 'Finance info',
    key: sidebarKeys.financeInfo,
    linkedRoutes: [theaterOrganizationFinanceInfo],
    icon: 'finance-info',
    internalRoute: {
      name: theaterOrganizationFinanceInfo,
    },
  },
  {
    label: 'Mailing list',
    key: sidebarKeys.mailingList,
    icon: 'mailing-list',
    linkedRoutes: [theaterOrganizationMailingList],
    internalRoute: {
      name: theaterOrganizationMailingList,
    },
  },
  {
    label: 'Genres and tags',
    key: sidebarKeys.genres,
    icon: 'genres-and-tags',
    subroutes: [
      {
        label: 'Genres',
        key: sidebarKeys.genres,
        linkedRoutes: [theaterOrganizationGenres],
        internalRoute: {
          name: theaterOrganizationGenres,
        },
      },
      {
        label: 'Tags',
        key: sidebarKeys.tags,
        linkedRoutes: [theaterOrganizationTags],
        internalRoute: {
          name: theaterOrganizationTags,
        },
      },
    ],
    linkedRoutes: [theaterOrganizationGenres, theaterOrganizationTags],
    internalRoute: {
      name: theaterOrganizationGenres,
    },
  },
  {
    label: 'Ticket types',
    key: sidebarKeys.ticketType,
    icon: 'ticket-types',
    linkedRoutes: [theaterOrganizationTicketType],
    internalRoute: {
      name: theaterOrganizationTicketType,
    },
  },
  {
    label: 'Subscriptions',
    key: 'sidebarKeys.organizationInfo',
    icon: 'subscriptions',
    subroutes: [
      {
        label: 'Subscriptions Plans',
        key: sidebarKeys.subscriptionsPlan,
        linkedRoutes: [theaterOrganizationSubscriptionsPlan],
        internalRoute: {
          name: theaterOrganizationSubscriptionsPlan,
        },
      },
      {
        label: 'Subscribers List',
        key: sidebarKeys.subscriptionsList,
        linkedRoutes: [theaterOrganizationSubscriptionsList],
        internalRoute: {
          name: theaterOrganizationSubscriptionsList,
        },
      },
    ],
    linkedRoutes: [theaterOrganizationSubscriptionsPlan, theaterOrganizationSubscriptionsList],
    internalRoute: {
      name: theaterOrganizationSubscriptionsPlan,
    },
  },
  {
    label: 'Promo codes',
    key: sidebarKeys.promoCodes,
    icon: 'promo-codes',
    linkedRoutes: [theaterOrganizationPromoCodes, theaterOrganizationPromoCodeInfo],
    internalRoute: {
      name: theaterOrganizationPromoCodes,
    },
  },
  {
    label: 'Memberships',
    key: 'sidebarKeys.memberships',
    icon: 'memberships',
    subroutes: [
      {
        label: 'Memberships',
        key: sidebarKeys.memberships,
        linkedRoutes: [theaterOrganizationMemberships],
        internalRoute: {
          name: theaterOrganizationMemberships,
        },
      },
      {
        label: 'Members List',
        key: sidebarKeys.membersList,
        linkedRoutes: [theaterOrganizationMembersList],
        internalRoute: {
          name: theaterOrganizationMembersList,
        },
      },
    ],
    linkedRoutes: [theaterOrganizationMemberships, theaterOrganizationMembersList],
    internalRoute: {
      name: theaterOrganizationMembersList,
    },
  },
  {
    label: 'Reports',
    key: sidebarKeys.reports,
    icon: 'reports',
    linkedRoutes: [theaterOrganizationReports],
    internalRoute: {
      name: theaterOrganizationReports,
    },
  },
]
